import Nouislider from 'nouislider-react'
import 'nouislider/distribute/nouislider.css'
import React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import './DateSlider.scss'
import { DateInterval } from './models/DateInterval'

interface IDateSliderProps extends WithTranslation {
  minDateInSlider?: Date
  maxDateInSLider?: Date
  onUpdate: (updates: DateInterval) => void
  startDate: Date
  endDate: Date
  includeLastYear?: boolean
  wholeRangeDrag?: boolean
}

interface IDateSliderState {
  months: string[]
  intervalMonths: Date[]
  lang: string
  dateIntervalForTooltips?: DateInterval
  tooltipsCoord?: { start: number; end: number }
}

class DateSlider extends React.Component<IDateSliderProps, IDateSliderState> {
  constructor(props: IDateSliderProps) {
    super(props)
    const { i18n } = this.props
    this.state = {
      months: [],
      intervalMonths: [],
      lang: i18n.language,
      dateIntervalForTooltips: {
        to: this.props.endDate,
        from: this.props.startDate,
      },
      tooltipsCoord: undefined,
    }
  }

  public componentDidMount() {
    this.setVars()
  }

  public componentDidUpdate(oldProps: IDateSliderProps) {
    const { startDate, endDate, i18n, minDateInSlider } = this.props
    const { lang } = this.state

    if (
      lang !== i18n.language ||
      startDate !== oldProps.startDate ||
      endDate !== oldProps.endDate ||
      minDateInSlider !== oldProps.minDateInSlider
    ) {
      this.setVars()
    }
  }

  public render() {
    const { startDate, endDate } = this.props
    const { months, intervalMonths, lang, tooltipsCoord } = this.state

    const tooltipPosition = () => {
      const max = Math.max(intervalMonths.length - 1, 1)
      const sameMonth = !!tooltipsCoord && tooltipsCoord.start - tooltipsCoord.end === 0

      const minOffset = sameMonth ? 0.8 : 4.4
      const maxOffset = sameMonth ? 99.2 : 95.6

      const startOffset = tooltipsCoord ? (tooltipsCoord?.start / max) * 100 : minOffset
      const endOffset = tooltipsCoord ? (tooltipsCoord?.end / max) * 100 : maxOffset
      const position = Math.max(minOffset, Math.min(maxOffset, (startOffset + endOffset) / 2))
      return { position, sameMonth }
    }
    return (
      <>
        {months.length > 0 && (
          <div className="date-slider">
            <div className="date-slider__slider">
              {!!this.state.dateIntervalForTooltips?.to && (
                <div className="tooltip" style={{ left: `${tooltipPosition().position}%` }}>
                  <div className="flex">
                    <div className="typeMile">
                      {this.formatDate(this.state.dateIntervalForTooltips.from)}
                      {!tooltipPosition().sameMonth && ` - ${this.formatDate(this.state.dateIntervalForTooltips.to)}`}
                    </div>
                  </div>
                </div>
              )}
              <Nouislider
                behaviour={this.props.wholeRangeDrag ? 'drag' : 'none'}
                key={lang}
                id={`month-slider`}
                range={{
                  min: 0,
                  max: intervalMonths.length - 1,
                }}
                start={[this.getIntervalPointFromDate(startDate), this.getIntervalPointFromDate(endDate)]}
                step={1}
                onUpdate={(values) => {
                  const intervalStartDate = this.getDateFromSlider(values[0])
                  const intervalEndDate = this.getDateFromSlider(values[1])
                  // Return interval and clicked marker in slider.
                  const newInterval: DateInterval = {
                    from: intervalStartDate,
                    to: new Date(intervalEndDate?.getFullYear(), intervalEndDate?.getMonth(), 1),
                  }
                  this.setState({
                    dateIntervalForTooltips: newInterval,
                    tooltipsCoord: {
                      start: Math.round(values[0]),
                      end: Math.round(values[1]),
                    },
                  })
                }}
                connect={true}
                onChange={(values) => {
                  const intervalStartDate = this.getDateFromSlider(values[0])
                  const intervalEndDate = this.getDateFromSlider(values[1])
                  // Return interval and clicked marker in slider.
                  const newInterval: DateInterval = {
                    from: intervalStartDate,
                    to: new Date(intervalEndDate?.getFullYear(), intervalEndDate?.getMonth(), 1),
                  }
                  this.props.onUpdate(newInterval)
                  this.setState({ dateIntervalForTooltips: newInterval })
                }}
                animate={true}
                pips={{
                  mode: 'values',
                  filter: (value: number, type: number) => this.filterPips(value, type),
                  density: 10,
                  values: intervalMonths.map((_, index) => index),
                  format: {
                    to: (value: number) => {
                      const selectedDate = this.getDateFromSlider(value)
                      if (intervalMonths.length === 12) {
                        return this.formatDate(selectedDate, true)
                      }

                      if (value % 12 === 0) {
                        return selectedDate.getFullYear()
                      }
                      return value
                    },
                    from: Number,
                  },
                }}
              />
            </div>
          </div>
        )}
      </>
    )
  }

  private setVars() {
    const { startDate, endDate, maxDateInSLider, includeLastYear, minDateInSlider, i18n, t } = this.props

    const totYears0 = minDateInSlider
      ? (maxDateInSLider || endDate)?.getFullYear() - minDateInSlider?.getFullYear() + 1
      : (maxDateInSLider || endDate)?.getFullYear() - startDate?.getFullYear() + 1
    const totYears = includeLastYear && totYears0 <= 1 ? totYears0 + 1 : totYears0
    const intervalMonths: Date[] = []
    const months = [
      t('JANUARY'),
      t('FEBRUARY'),
      t('MARCH'),
      t('APRIL'),
      t('MAY'),
      t('JUNE'),
      t('JULY'),
      t('AUGUST'),
      t('SEPTEMBER'),
      t('OCTOBER'),
      t('NOVEMBER'),
      t('DECEMBER'),
    ]

    // Count total months to set in slider.
    for (let i = 0; i < totYears; i++) {
      const year = minDateInSlider
        ? minDateInSlider.getFullYear() + i - (totYears > totYears0 ? 1 : 0)
        : startDate.getFullYear() + i - (totYears > totYears0 ? 1 : 0)
      for (let m = 0; m <= 11; m++) {
        const date = new Date(year, m, 1)
        intervalMonths.push(date)
      }
    }

    this.setState({
      intervalMonths,
      months,
      lang: i18n.language,
    })
  }

  private formatDate(date: Date, isSliderLabel = false) {
    const { months } = this.state
    if (date === undefined) {
      return ''
    }
    if (isSliderLabel) {
      return months[date?.getMonth()]?.substr(0, 3)
    }
    return `${months[date?.getMonth()]} ${date.getFullYear()}`
  }

  private getDateFromSlider(value: number): Date {
    const { intervalMonths } = this.state
    return intervalMonths[Math.round(value)]
  }

  private getIntervalPointFromDate(value: Date): number {
    const { intervalMonths } = this.state
    const montInInterval = intervalMonths.find(
      (d) => d && value && d.getMonth() === value.getMonth() && d.getFullYear() === value.getFullYear()
    )

    return intervalMonths.indexOf(montInInterval!)
  }

  private filterPips(value: number, type: number) {
    const { intervalMonths } = this.state
    if (intervalMonths.length === 12) {
      return type
    }
    return value % 12 === 0 ? type : 0
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export default withTranslation('common')(DateSlider)
